import { Dialog } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { cloneDeep, isEmpty } from 'lodash';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import { getBidderGroups, IBidderGroup, updateBidderGroups } from '~/src/dash/api/bidder-group';
import { IBidderGroupLabel } from '~/src/dash/api/bidder-group-label';
import { getChannelGroupsPromise } from '~/src/dash/api/channel-group';
import { getSitesPromise, simplifiedChannelsKeys } from '~/src/dash/api/channels';
import {
  deleteHeaderBidder,
  IBidderLabel,
  IChannelGroup,
  IHeaderBidder,
  ISite,
  ResourceActiveDisabled,
  ResourceActiveEnabled,
  updateHeaderBidder,
} from '~/src/dash/api/console-api';
import {
  getHeaderbiddersWithPagination,
  simplifiedHeaderBidderPreloads,
  simplifiedHeaderBidderUiFields,
} from '~/src/dash/api/headerbidders/headerbidders_methods';
import {
  HeaderBiddersAPIKey,
  IHeaderBidderListInfo,
} from '~/src/dash/api/headerbidders/headerbidders_models';
import { EBool } from '~/src/dash/api/types/bool';
import { IFlexibleBiddingThreshold } from '~/src/dash/api/types/flexible_bidding';
import AddToBidderGroupDialog from '~/src/dash/components/add-to-bidder-group-dialog';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { EPriorities } from '~/src/dash/components/filters/common';
import { formatSelectedFiltersIntoQueryObject } from '~/src/dash/components/filters/conversions';
import { FilterRowV2 } from '~/src/dash/components/filters/FilterRowV2';
import { GroupSettingsDialog } from '~/src/dash/components/group-settings-dialog';
import { SearchResultsText } from '~/src/dash/components/input/common';
import { MenuButton } from '~/src/dash/components/menu-button';
import { ModalSelectTable } from '~/src/dash/components/modal-select-table';
import { ModelDialogDeleteView } from '~/src/dash/components/model-dialog-delete-view';
import { extractSortsFromColumns } from '~/src/dash/components/sorts/common';
import { SortByRow } from '~/src/dash/components/sorts/SortByRow';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractDefaultActiveColumnKeys,
  extractVisibleColumns,
  ICustomRenderCellJSXProps,
  IListViewTableColumnInfo,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { ColumnSelector } from '~/src/dash/components/tables/ListViewTable/linked_components/ColumnSelector';
import { fontSizing } from '~/src/dash/components/text/common';
import { FlexibleBiddingDialog } from '~/src/dash/components/tier-free/flexible-bidding-dialog';
import { IconTextToggle } from '~/src/dash/components/toggles/IconTextToggle';
import {
  ButtonV2,
  Col,
  Cols,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  spacing,
  Text,
  TextInput,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  navigateToBulkUpdateRoute,
  RouteBidderBulkUpdate,
  RouteBidderBulkUpload,
  RouteBidderSettingsClone,
  RouteBidderSettingsExisting,
  RouteBidderSettingsNew,
  RouteChannelExisting,
} from '~/src/dash/router/routes';
import { IMenuList } from '~/src/dash/types/menuList';
import { ChipInactive } from '~/src/dash/utils/campaign-status';
import { muiColors, publicaColors } from '~/src/dash/utils/colors';
import { DemandSources } from '~/src/dash/globals/demand-sources';
import { getHeaderBidderAdaptersForPublisher } from '~/src/dash/utils/header-bidder-adapters';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { obfuscateBidderGroupName, obfuscateBidderName } from '~/src/dash/utils/obfuscate';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { isDemoAccount } from '~/src/dash/utils/ui';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import { formatBiddersResponseIntoLVTRows } from '~/src/dash/views/bidders/Bidders/tabs/BiddersListView/config';
import { useBiddersListStore } from '~/src/dash/views/bidders/contexts';
import { getBiddersList } from '~/src/dash/views/common/dashboards-consts';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

enum ESelectedModal {
  TargetedChannels = 1,
  TargetedChannelGroups = 2,
  AddToBidderGroup = 3,
  GroupEditor,
}

const BULK_ACTION_COLOR = publicaColors.blue;

const removeKeyFromArray = (array: string[], key: string) => {
  const index = array.indexOf(key);
  if (index > -1) {
    array.splice(index, 1);
  }
  return array;
};

interface IBulkButtonProps {
  selectedBidderIDs: string[] | number[];
  session: ISessionState;
  user: IUserState;
}

const BulkUpdateButton = ({ selectedBidderIDs, session, user }: IBulkButtonProps) => {
  return (
    <ButtonV2
      clearBackground
      allCaps
      onClick={() =>
        navigateToBulkUpdateRoute(
          RouteBidderBulkUpdate,
          selectedBidderIDs,
          extractRouteParams({ session, user }),
        )
      }
      style={{ padding: '5px 16px 5px 12px' }}
    >
      <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
        <Col paddingTop='6px'>
          <EditIcon
            style={{
              height: '16px',
              color: BULK_ACTION_COLOR,
            }}
          />
        </Col>
        <Col>
          <Text small bold color={BULK_ACTION_COLOR} tKey='BulkUpdate' />
        </Col>
      </Cols>
    </ButtonV2>
  );
};

export const BiddersListView = () => {
  const { session, user } = useBaseStore();
  const { t } = usePublicaTranslation();
  const { readAuthorization, writeAuthorization } = useBiddersListStore();
  // get config
  const { Bidders } = useBaseConfig();
  const cfg = Bidders.All;
  const { cypressClassName, defaultState } = cfg;
  const publisherTimezone = session.publisher.Timezone;
  const isSuperUser = hasRoleSuperUser(user.user);

  // TODO: remove in the future. this is too custom.
  const isSamsung = session.publisher.ID === '394';

  cfg.isInitialized = true;

  // states and refs
  const isMounted = useRef(false);
  const [responseData, setResponseData] = useState<IHeaderBidderListInfo[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  // loading
  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  // snackbar
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);
  // search
  const [query, setQuery] = useState('');
  const [isBidderParamsSearchEnabled, setIsBidderParamsSearchEnabled] = useState(false);
  const [bidderParamsQuery, setBidderParamsQuery] = useState('');
  // filters
  const [filtersLoadState, setFiltersLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [filters, setFilters] = useState<Record<string, ISelectOption[]>>({});
  const [selectedFilters, setSelectedFilters] = useState<Record<string, Set<string | number>>>({});
  // table states
  const [activeColumnKeys, setActiveColumnKeys] = useState<string[]>(
    defaultState.activeColumnKeys || extractDefaultActiveColumnKeys(cfg.table.columns),
  );
  const [activeColumnsList, setActiveColumnsList] = useState<IMenuList>(
    extractVisibleColumns(cfg.table.columns, defaultState.activeColumnKeys || [], isSuperUser),
  );
  const [isTableActionPending, setIsTableActionPending] = useState(false);
  const [order, setOrder] = useState<Order>(defaultState.order || 'asc');
  const [orderBy, setOrderBy] = useState<string>(defaultState.orderBy || '');
  const [page, setPage] = useState(defaultState.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultState.rowsPerPage || 10);
  // bulk
  const [selected, setSelected] = useState<string[]>([]);
  const [deleteBulkModalActive, setIsDeleteModalActive] = useState<boolean>(false);
  // add to bidder list
  const [isAddToBidderGroupModalActive, setIsAddToBidderGroupModalActive] =
    useState<boolean>(false);
  const [bidderGroupsList, setBidderGroupsList] = useState<ISelectOption[]>([]);
  const [tempGroups, setTempGroups] = useState<IBidderGroup[]>([]);
  const [forceMenuButtonClosed, setForceMenuButtonClosed] = useState<boolean>(false);
  // new bidder group
  const [responseGroupData, setResponseGroupData] = useState<IBidderGroup[]>([]);
  const [isGroupSettingsModalActive, setIsGroupSettingsModalActive] = useState<boolean>(false);
  const [activeGroup, setActiveGroup] = useState<IBidderGroup | undefined>(undefined);
  const [biddersList, setBiddersList] = useState<IMenuList>({});
  const [filteredBiddersList, setFilteredBiddersList] = useState<IMenuList>({});
  const [selectedBidderGroupLabels, setSelectedBidderGroupLabels] = useState<IBidderGroupLabel[]>(
    [],
  );
  const [allGroupLabels, setAllGroupLabels] = useState<IBidderGroupLabel[]>([]);
  // modal states
  const [activeBidder, setActiveBidder] = useState<IHeaderBidderListInfo | undefined>(undefined);
  const [activeModal, setActiveModal] = useState<ESelectedModal | undefined>(undefined);
  const [activeTargeted, setActiveTargeted] = useState<ISite[] | IChannelGroup[]>([]);
  const [isChannelsModalActive, setIsChannelsModalActive] = useState<boolean>(false);
  const [isChannelGroupsModalActive, setIsChannelGroupsModalActive] = useState<boolean>(false);
  // flexible bidder modal
  const [isTierChangeWarningActive, setIsTierChangeWarningActive] = useState<boolean>(false);
  const [flexibleBiddingInvalidThresholds, setFlexibleBiddingInvalidThresholds] = useState<
    IFlexibleBiddingThreshold[]
  >([]);
  const [flexibleBiddingNewPriority, setFlexibleBiddingNewPriority] = useState<number>(0);
  const [flexibleBiddingBidder, setFlexibleBiddingBidder] = useState(
    {} as ICustomRenderCellJSXProps,
  );

  // other consts
  const sorts = extractSortsFromColumns(cfg.sorts, cfg.table.columns);
  const isShowTrendsActive = activeColumnKeys.some(
    (key) => (cfg.trends as string[]).indexOf(key) > -1,
  );
  const isDemoUser = isDemoAccount(user.user.Username);

  const populateFilters = (): void => {
    setFiltersLoadState(LoadingStateLoading);
    const keys: string[] = [];
    const getListFns: Array<Promise<ISelectOption[]>> = [];

    Object.values(cfg.filters).forEach((filter) => {
      keys.push(filter.key);
      getListFns.push(filter.getList(session, user));
    });

    const tempFilters: Record<string, ISelectOption[]> = {};
    const tempSelectedFilters: Record<string, Set<string | number>> = {};

    Promise.all(getListFns)
      .then((results) => {
        results.forEach((result, index) => {
          const filterKey = keys[index];
          tempFilters[filterKey] = result;
          tempSelectedFilters[filterKey] =
            cfg.filters[filterKey].selectedOptionIDs || new Set<string | number>();
        });
        unstable_batchedUpdates(() => {
          setFilters(tempFilters);
          setFiltersLoadState(LoadingStateHasData);
          setSelectedFilters(tempSelectedFilters);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setFilters({});
          setSelectedFilters({});
          setFiltersLoadState(LoadingStateNoData);
          setSnackbarError(true);
          setSnackbarMessage(t('SnackbarErrorRetrievingItem', { item: t('Filters') }));
        });
      });
  };

  // handles
  const getData = (): void => {
    // log query start time
    const startTime = moment().unix();
    setLoadState(LoadingStateLoading);
    const filterParams = formatSelectedFiltersIntoQueryObject(selectedFilters, cfg.filters);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const hasDemandSource = filterParams?.demandSources?.length === 1;
    setIsBidderParamsSearchEnabled(hasDemandSource);

    // create query object
    const urlQueries = {
      bidderParamsSearch: hasDemandSource ? bidderParamsQuery : '',
      pageSize: rowsPerPage,
      bidderGroupIDs: [],
      page,
      search: query,
      idSearch: query,
      orderBy,
      orderDirection: order,
      ...filterParams,
    };

    // update config
    cfg.defaultState.orderBy = orderBy;
    cfg.defaultState.order = order;
    (cfg.defaultState.page = page), (cfg.defaultState.rowsPerPage = rowsPerPage);

    getHeaderbiddersWithPagination(session.publisher.ID, {
      urlQueries,
      selectedChannelIds: filterParams.selectedChannelIDs || [],
      preloads: [
        simplifiedHeaderBidderPreloads.SITE_IDS,
        simplifiedHeaderBidderPreloads.CHANNEL_GROUP_IDS,
        simplifiedHeaderBidderPreloads.HEADER_BIDDER_PAST_WEEK_SUMMARY,
        simplifiedHeaderBidderPreloads.BIDDER_LABELS,
      ],
      uiFields: [
        simplifiedHeaderBidderUiFields.ID,
        simplifiedHeaderBidderUiFields.NAME,
        simplifiedHeaderBidderUiFields.ACTIVE,
        simplifiedHeaderBidderUiFields.BIDDER,
        simplifiedHeaderBidderUiFields.PRIORITY,
        simplifiedHeaderBidderUiFields.FLEXIBLE_BIDDING_ENABLED,
        simplifiedHeaderBidderUiFields.FLEXIBLE_BIDDING_THRESHOLDS,
        simplifiedHeaderBidderUiFields.SUPER_ONLY,
        simplifiedHeaderBidderUiFields.CREATED_AT,
        simplifiedHeaderBidderUiFields.UPDATED_AT,
      ],
    }).then(([headerBiddersWithCount, err]) => {
      // handle error from getHeaderbiddersWithPagination
      if (err !== undefined) {
        // add track event
        unstable_batchedUpdates(() => {
          setSnackbarMessage(t('SnackbarErrorRetrievingData'));
          setSnackbarError(true);
          setLoadState(LoadingStateNoData);
        });
        return;
      }

      const data: IHeaderBidderListInfo[] =
        (headerBiddersWithCount && headerBiddersWithCount.HeaderBidderListInfo) || [];
      // no error add track event

      if (headerBiddersWithCount) {
        // make necessary conversions before storing
        data.forEach((item) => {
          // obfuscate bidder name if needed
          if (isDemoUser) {
            item.Name = obfuscateBidderName(
              item as any as IHeaderBidder,
              user.user.Username,
              cfg.table.columns.Name.responseKey,
            );
          }
          // get bidder labels
          const labelsArr: string[] = [];
          if (item.BidderLabels != null) {
            const labels: IBidderLabel[] = item.BidderLabels as IBidderLabel[];
            labels.forEach((ele) => {
              labelsArr.push(ele.Name);
            });
            item.BidderLabelsArray = labelsArr;
          }

          // convert timestamps to publisher timezone
          item.CreatedAt = moment.utc(item.CreatedAt).tz(publisherTimezone).format();
          item.UpdatedAt = moment.utc(item.UpdatedAt).tz(publisherTimezone).format();
          // get the demand source for the bidder
          for (const biddercode of getHeaderBidderAdaptersForPublisher(
            '',
            false,
            session.publisher,
            isSuperUser,
          )) {
            if (item.Bidder === biddercode.key) {
              item.Bidder = biddercode.text;
            }
          }
          if (item.Bidder === DemandSources.verizon) {
            item.Bidder = 'Yahoo';
          }
          // flatten HeaderBidderPastWeekSummary
          const hasPastWeekSummary =
            !!item.HeaderBidderPastWeekSummary && !isEmpty(item.HeaderBidderPastWeekSummary);
          item.PastWeekAverageCPM = hasPastWeekSummary
            ? item.HeaderBidderPastWeekSummary.PastWeekAverageCPM
            : null;
          item.PastWeekBidRequests = hasPastWeekSummary
            ? item.HeaderBidderPastWeekSummary.PastWeekBidRequests
            : null;
          item.PastWeekImpressions = hasPastWeekSummary
            ? item.HeaderBidderPastWeekSummary.PastWeekImpressions
            : null;
          item.PastWeekImpressionsTrend = hasPastWeekSummary
            ? item.HeaderBidderPastWeekSummary.PastWeekImpressionsTrend
            : null;
          item.PastWeekRevenue = hasPastWeekSummary
            ? item.HeaderBidderPastWeekSummary.PastWeekRevenue
            : null;
          item.PastWeekRevenueTrend = hasPastWeekSummary
            ? item.HeaderBidderPastWeekSummary.PastWeekRevenueTrend
            : null;
        });
        const loadTime = moment().unix() - startTime;
        track(
          'Bidder List - List Loaded',
          {
            isSuperUser,
            loadTime,
            numRowsDisplayed: data.length,
            totalRowCount: headerBiddersWithCount.TotalCount,
          },
          user.user,
        );
        unstable_batchedUpdates(() => {
          setResponseData(data);
          setLoadState(LoadingStateHasData);
          setTotalRowCount(headerBiddersWithCount.TotalCount || 0);
        });
      }
    });
  };

  const getBidderGroupsList = (groupSelection: number) => {
    setLoadState(LoadingStateLoading);
    if (readAuthorization.isAuthorized) {
      getBidderGroups({
        query: {
          publisher_id: session.publisher.ID,
        },
      })
        .then(({ bidderGroups }: { bidderGroups: IBidderGroup[] }) => {
          const bidderGroupsList: ISelectOption[] = [];
          bidderGroups.forEach((group) => {
            bidderGroupsList.push({
              id: group.ID,
              label: group.Name,
            });
          });
          unstable_batchedUpdates(() => {
            setBidderGroupsList(bidderGroupsList);
            setTempGroups(bidderGroups);
            setForceMenuButtonClosed(true);
          });
          if (groupSelection === ESelectedModal.AddToBidderGroup) {
            setLoadState(LoadingStateHasData);
            setIsAddToBidderGroupModalActive(true);
          } else if (groupSelection === ESelectedModal.GroupEditor) {
            getBiddersList(session, user).then((result) => {
              setBiddersList(result.Bidders);
              const clonedBidderGroups = bidderGroups;
              const newGroup = {
                ID: '0',
                Name: '',
                BidderIDs: selected.join(','),
                BidderGroupLabels: [] as IBidderGroupLabel[],
              } as IBidderGroup;
              clonedBidderGroups.push(newGroup);
              unstable_batchedUpdates(() => {
                setActiveGroup(newGroup);
                setTempGroups(clonedBidderGroups);
                setLoadState(LoadingStateHasData);
              });
              setIsGroupSettingsModalActive(true);
              setForceMenuButtonClosed(true);
            });
          }
        })
        .catch((err) => {
          console.error(err);
          unstable_batchedUpdates(() => {
            setResponseData([]);
            setLoadState(LoadingStateNoData);
            setSnackbarMessage(t('SnackbarErrorRetrievingData'));
            setSnackbarError(true);
          });
        });
    }
  };

  // updateHeaderBidder is called when the various handler functions around the ListTable component want to make
  // changes on the bidder.
  const patchHeaderBidder = (override: Record<string, any>, props: ICustomRenderCellJSXProps) => {
    const bidderID = props.row.ID.value;
    if (bidderID && bidderID !== '0') {
      const bidderName = props.row.Name.value;
      const headerBidder = { ID: bidderID } as IHeaderBidderListInfo;
      setIsTableActionPending(true);
      updateHeaderBidder({
        query: {
          publisher_id: session.publisher.ID,
          bidder_id: bidderID,
          ignore_missing_fields: true,
          update_dates: false,
        },
        body: {
          ignore_missing_fields: true,
          headerbidder: Object.assign(headerBidder, override),
        },
      })
        .then((patchedHeaderBidder: IHeaderBidder) => {
          unstable_batchedUpdates(() => {
            setIsTableActionPending(false);
            setSnackbarMessage(
              t('SnackbarSuccessfullyUpdatedIDName', {
                id: patchedHeaderBidder.ID,
                name: patchedHeaderBidder.Name,
              }),
            );
            setSnackbarError(false);
          });
          getData();
        })
        .catch((err: Error) => {
          console.error(err);
          unstable_batchedUpdates(() => {
            setIsTableActionPending(false);
            setSnackbarMessage(
              t('SnackbarFailedToUpdateIDName', {
                id: String(bidderID),
                name: bidderName as string,
              }),
            );
            setSnackbarError(true);
          });
        });
    } else {
      unstable_batchedUpdates(() => {
        setIsTableActionPending(false);
        setSnackbarMessage(t('SnackbarSelectedItemDoesNotExist', { item: t('Bidder') }));
        setSnackbarError(true);
      });
    }
  };

  // this sets up the changes to force a re-sort
  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo) => {
    const { orderByKey, requestKey, defaultOrderDirection, label } = column;
    track('Bidder List - Column Header Sort', { isSuperUser, sortBy: label }, user.user);
    const key = orderByKey || requestKey;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setSelected([]);
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setSelected([]);
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangePage = (_e: unknown, newPage: number) => {
    unstable_batchedUpdates(() => {
      setSelected([]);
      setPage(newPage);
    });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    track(
      'Bidder List - Change Rows Per Page',
      { isSuperUser, rowsPerPage: updatedRowsPerPage },
      user.user,
    );
    unstable_batchedUpdates(() => {
      setSelected([]);
      setRowsPerPage(updatedRowsPerPage);
      setPage(0);
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarMessage('');
  };

  const handleOnChangeQuery = (text: string) => {
    track('Bidder List - Search', { isSuperUser }, user.user);
    setQuery(text);
  };

  const handleOnChangeBidderParamsQuery = (text: string) => {
    setBidderParamsQuery(text);
  };

  // filter functions
  const handleFilterAccept = (value: Set<number | string>, key: string): void => {
    if (
      selectedFilters[key].size === value.size &&
      [...selectedFilters[key]].every((item) => value.has(item))
    ) {
      // No change, do nothing
      return;
    } else {
      const clonedSelectedFilters = { ...selectedFilters };
      clonedSelectedFilters[key] = value;
      setSelectedFilters(clonedSelectedFilters);
      cfg.filters[key].selectedOptionIDs = value;
      track('Bidder List - Use Filter', { isSuperUser, filter: key }, user.user);
    }
  };

  const handleClearFilters = (): void => {
    const clearedSelectedFilters: Record<string, Set<string | number>> = {};
    Object.values(cfg.filters).forEach((filter) => {
      clearedSelectedFilters[filter.key] = new Set<string | number>(
        filter.selectedOptionIDsOnReset || [],
      );
    });
    setSelectedFilters(clearedSelectedFilters);
  };

  const handleSelectSortBy = (orderByKey: string) => {
    track('Bidder List - Sort Row', { isSuperUser, orderByKey }, user.user);
    unstable_batchedUpdates(() => {
      setSelected([]);
      setOrder(cfg.table.columns[orderByKey].defaultOrderDirection || 'asc');
      setOrderBy(cfg.table.columns[orderByKey].orderByKey || orderByKey);
      setPage(0);
    });
  };

  const handleOnChangeStatus = (_id: string, checked: boolean, row: IListViewTableRow) => {
    track('Bidder List - Click Active Toggle', { isSuperUser }, user.user);
    const value = checked ? ResourceActiveEnabled : ResourceActiveDisabled;
    patchHeaderBidder({ Active: value }, { row, value });
  };

  const handleChangePriority = (newValue: string, props: ICustomRenderCellJSXProps) => {
    const priorityValue = EPriorities[newValue];
    if (!props.row.FlexibleBiddingEnabled.value) {
      track('Bidder List - Modify Tier', { isSuperUser }, user.user);
      patchHeaderBidder({ Priority: priorityValue }, props);
    } else {
      const thresholds = props.row.FlexibleBiddingThresholds.value as IFlexibleBiddingThreshold[];
      const invalidThresholds = thresholds.filter(
        (threshold) => threshold.BidderPriority <= priorityValue,
      );
      if (!isEmpty(invalidThresholds)) {
        unstable_batchedUpdates(() => {
          setIsTierChangeWarningActive(true);
          setFlexibleBiddingInvalidThresholds(invalidThresholds);
          setFlexibleBiddingNewPriority(priorityValue);
          setFlexibleBiddingBidder(props);
        });
      } else {
        track('Bidder List - Modify Tier', { isSuperUser }, user.user);
        patchHeaderBidder({ Priority: priorityValue }, props);
        setIsTierChangeWarningActive(false);
      }
    }
  };

  const removeInvalidFlexibleBiddingThresholdsAndUpdate = () => {
    const { row } = flexibleBiddingBidder;
    const validThresholds = (
      row.FlexibleBiddingThresholds.value as IFlexibleBiddingThreshold[]
    ).filter((threshold) => threshold.BidderPriority > flexibleBiddingNewPriority);
    track('Bidder List - Modify Tier', { isSuperUser }, user.user);
    patchHeaderBidder(
      {
        Priority: flexibleBiddingNewPriority,
        FlexibleBiddingEnabled:
          validThresholds && validThresholds.length > 0 ? EBool.EBool_TRUE : EBool.EBool_FALSE,
        FlexibleBiddingThresholds: validThresholds,
      },
      flexibleBiddingBidder,
    );
  };

  const handleChangeSuperUserOnly = (newValue: boolean, props: ICustomRenderCellJSXProps) => {
    patchHeaderBidder({ SuperOnly: newValue }, props);
  };

  const handleCloneItem = (id: string) => {
    track('Bidder List - Clone Bidder', { isSuperUser }, user.user);
    navigate(RouteBidderSettingsClone, extractRouteParams({ session, user }), { b: id });
  };

  const handleDeleteItem = (id: string) => {
    setIsTableActionPending(true);
    deleteHeaderBidder({
      query: {
        publisher_id: session.publisher.ID,
        bidder_id: id,
      },
    })
      .then(() => {
        track('Bidder List - Delete Bidder', { isSuperUser }, user.user);
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage(t('SnackbarSuccessfullyDeletedItem', { item: t('Bidder') }));
          setSnackbarError(false);
        });
        getData();
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage(t('SnackbarFailedToDeleteItem', { item: t('Bidder') }));
          setSnackbarError(true);
        });
      });
    return new Promise((resolve) => resolve(undefined));
  };

  const handleClickOnChannelsList = (id: string) => {
    const clonedData = cloneDeep(responseData);
    const selectedBidder = clonedData.find(
      (headerBidder: IHeaderBidderListInfo) => headerBidder.ID.toString() === id.toString(),
    );
    track('Bidder List - Open Modal', { isSuperUser, modal: 'Targeted Channels' }, user.user);
    unstable_batchedUpdates(() => {
      setActiveBidder(selectedBidder);
      setActiveModal(ESelectedModal.TargetedChannels);
    });
  };

  const handleClickOnChannelGroupsList = (id: string) => {
    const clonedData = cloneDeep(responseData);
    const selectedBidder = clonedData.find(
      (headerBidder: IHeaderBidderListInfo) => headerBidder.ID.toString() === id.toString(),
    );
    track('Bidder List - Open Modal', { isSuperUser, modal: 'Targeted Channel Groups' }, user.user);
    unstable_batchedUpdates(() => {
      setActiveBidder(selectedBidder);
      setActiveModal(ESelectedModal.TargetedChannelGroups);
    });
  };

  const handleCloseModals = () => {
    unstable_batchedUpdates(() => {
      setActiveBidder(undefined);
      setActiveModal(undefined);
      setActiveTargeted([]);
      setIsChannelsModalActive(false);
      setIsChannelGroupsModalActive(false);
      setIsAddToBidderGroupModalActive(false);
      setIsGroupSettingsModalActive(false);
    });
  };

  const handleAcceptCreateNewBidderSelect = (list: IMenuList) => {
    setFilteredBiddersList(list);
  };

  const handleAcceptAddToBidderGroup = (selectedGroup: Set<number>) => {
    const selectedGroupId = selectedGroup.size > 0 ? [...selectedGroup][0] : 0;
    const selectedActiveGroup = tempGroups.find(
      (group) => Number(group.ID) === selectedGroupId,
    ) as IBidderGroup;
    setActiveGroup(selectedActiveGroup);
  };

  // active columns
  const handleChangeActiveColumns = (keys: string[]) => {
    setActiveColumnKeys(keys);
  };

  const handleAcceptActiveColumns = (keys: string[]) => {
    track('Bidder List - Select Active Columns', { isSuperUser, activeColumns: keys }, user.user);
    setActiveColumnKeys(keys);
  };

  const handleChangeBidderGroup = (name: string, bidderGroupID: number) => {
    const clonedBidderGroups = cloneDeep(tempGroups);
    const selectedGroup = clonedBidderGroups.find((group) => group.ID === bidderGroupID.toString());
    const selectedBidderIDs: string[] = activeGroup ? activeGroup.BidderIDs.split(',') : [];
    if (bidderGroupID === 0) {
      for (const { ID, selected } of Object.values(filteredBiddersList)) {
        if (selected) {
          selectedBidderIDs.push(ID);
        }
      }
    }
    selected.forEach((ID) => {
      selectedBidderIDs.push(ID);
    });
    // delete duplicated values
    const uniqueBidderIDs = [...new Set(selectedBidderIDs)];
    if (selectedGroup) {
      (selectedGroup.Name = selectedGroup.Name ? selectedGroup.Name : name),
        (selectedGroup.BidderIDs = uniqueBidderIDs.join(',')),
        (selectedGroup.BidderGroupLabels = selectedBidderGroupLabels);
      // error checks
      if (!selectedGroup.Name) {
        unstable_batchedUpdates(() => {
          setSnackbarError(true);
          setSnackbarMessage(t('BiddersSnackbarBidderGroupMustHaveName'));
        });
        return;
      }
      if (!selectedGroup.BidderIDs) {
        unstable_batchedUpdates(() => {
          setSnackbarError(true);
          setSnackbarMessage(t('BiddersSnackbarBidderGroupMustHaveBidders'));
        });
        return;
      }
    }
    setTempGroups(clonedBidderGroups);
    patchBidderGroups(clonedBidderGroups);
  };

  const handleChangeSelectedBidderGroupLabels = (labels: IBidderGroupLabel[]) => {
    // map any string IDs to integers before updating state
    const mappedBidderGroupLabels = labels.map((label) => {
      if (typeof label.ID === 'string') {
        return Object.assign(label, { ID: parseInt(label.ID, 10) });
      } else {
        return label;
      }
    });
    setSelectedBidderGroupLabels(mappedBidderGroupLabels);
  };

  const patchBidderGroups = (updatedGroups: IBidderGroup[]) => {
    updateBidderGroups({
      query: {
        publisher_id: session.publisher.ID,
        bidder_groups: updatedGroups.map((group: IBidderGroup) => {
          group.BidderIDs = group.BidderIDs.split(',')
            .filter((id: string) => id !== 'Select All')
            .join(',');
          return group;
        }),
      },
    })
      .then(({ bidderGroups }: { bidderGroups: IBidderGroup[] }) => {
        bidderGroups.forEach((group) => {
          // obfuscate if needed
          if (isDemoUser) {
            group.Name = obfuscateBidderGroupName(
              group as any as IBidderGroup,
              user.user.Username,
              cfg.table.columns.Name.responseKey,
            );
          }
        });
        unstable_batchedUpdates(() => {
          setSnackbarMessage(
            t('SnackbarSuccessfullyUpdatedItem', {
              item: t('BidderGroups'),
            }),
          );
          setSnackbarError(false);
          setResponseGroupData(bidderGroups);
        });
        handleCloseModals();
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setSnackbarMessage(t('SnackbarFailedToUpdateItem', { item: t('BidderGroups') }));
          setSnackbarError(true);
        });
      });
    return new Promise((resolve) => resolve(undefined));
  };

  const extractAllBidderGroupLabels = (groups: IBidderGroup[]) => {
    const allLabels: IBidderGroupLabel[] = [];
    const groupLabels = new Set();
    for (const group of groups) {
      if (group.BidderGroupLabels && group.BidderGroupLabels.length) {
        for (const bidderGroupLabel of group.BidderGroupLabels) {
          if (!groupLabels.has(bidderGroupLabel.ID)) {
            groupLabels.add(bidderGroupLabel.ID);
            allLabels.push(bidderGroupLabel);
          }
        }
      }
    }
    return allLabels;
  };

  const handleOnClickShowTrends = () => {
    const trendKeys = cloneDeep(cfg.trends as string[]);
    const updatedActiveColumnKeys: string[] = [];
    const columns = cfg.table.columns;
    if (isShowTrendsActive) {
      activeColumnKeys.forEach((key) => {
        if (!trendKeys.includes(key)) {
          updatedActiveColumnKeys.push(key);
        }
      });
    } else {
      track('Bidder List - Show Trends', { isSuperUser }, user.user);
      activeColumnKeys.forEach((key) => {
        const trendKey = columns[key].trendKey;
        if (trendKey) {
          updatedActiveColumnKeys.push(key, trendKey);
          removeKeyFromArray(trendKeys, trendKey);
        } else {
          updatedActiveColumnKeys.push(key);
        }
      });
      if (!isEmpty(trendKeys)) {
        trendKeys.forEach((key) => {
          const parentKey = columns[key].parentKey;
          if (parentKey) {
            updatedActiveColumnKeys.push(parentKey, key);
          }
        });
      }
    }
    unstable_batchedUpdates(() => {
      setActiveColumnKeys(updatedActiveColumnKeys);
      setActiveColumnsList(
        extractVisibleColumns(cfg.table.columns, updatedActiveColumnKeys || [], isSuperUser),
      );
    });
  };

  const getBidderSettingsRoute = (id: string): string => {
    return getRoutePath(RouteBidderSettingsExisting, extractRouteParams({ session, user }), {
      b: id,
    });
  };

  useEffect(() => {
    track('Bidder List - View', { isSuperUser }, user.user);
    populateFilters();
  }, []);

  // recall api whenever these dependencies change
  useEffect(() => {
    if (isMounted.current) {
      getData();
    }
  }, [selectedFilters, order, orderBy, page, query, bidderParamsQuery, rowsPerPage]);

  useEffect(() => {
    if (isMounted.current && activeBidder && !isEmpty(activeBidder)) {
      // if targeted channels click
      if (activeModal === ESelectedModal.TargetedChannels && activeBidder.SiteIDS) {
        getSitesPromise({
          publisherIDs: [session.publisher.ID],
          publisherID: session.publisher.ID,
          siteIDs: activeBidder?.SiteIDS || [],
          uiFields: [simplifiedChannelsKeys.ID, simplifiedChannelsKeys.NAME],
        })
          .then((result) => {
            unstable_batchedUpdates(() => {
              setActiveTargeted(result);
            });
          })
          .catch((err) => {
            console.error(err);
          });
      } else {
        setActiveTargeted([]);
      }
      // if channel groups click
      if (
        activeModal === ESelectedModal.TargetedChannelGroups &&
        activeBidder &&
        activeBidder.ChannelGroupIDS
      ) {
        getChannelGroupsPromise({
          publisherID: session.publisher.ID,
          channelGroupIDs: activeBidder?.ChannelGroupIDS || [],
          uiFields: ['id', 'name'],
        })
          .then((result: { channelGroups: IChannelGroup[] }) => {
            const channelGroups = result ? result.channelGroups || [] : [];
            unstable_batchedUpdates(() => {
              setActiveTargeted(channelGroups);
            });
          })
          .catch((err) => {
            console.error(err);
          });
      } else {
        setActiveTargeted([]);
      }
    }
  }, [activeBidder, activeModal, session.publisher.ID]);

  useEffect(() => {
    if (isMounted.current && !!activeBidder) {
      switch (activeModal) {
        case ESelectedModal.TargetedChannels:
          setIsChannelsModalActive(true);
          break;
        case ESelectedModal.TargetedChannelGroups:
          setIsChannelGroupsModalActive(true);
          break;
        default:
          break;
      }
    }
  }, [activeTargeted]);

  // update config on selected columns change
  useEffect(() => {
    if (isMounted.current) {
      cfg.defaultState.activeColumnKeys = activeColumnKeys as HeaderBiddersAPIKey[];
    }
  }, [activeColumnKeys]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    }
  }, []);

  // filter data via search query
  useEffect(() => {
    if (isMounted.current) {
      const clonedData = cloneDeep(responseGroupData);
      unstable_batchedUpdates(() => {
        setAllGroupLabels(extractAllBidderGroupLabels(responseGroupData));
        setTempGroups(clonedData);
      });
    }
  }, [responseGroupData]);

  // once an activeGroup is selected, we correct the bidder list
  useEffect(() => {
    if (isMounted.current && activeGroup && !isEmpty(activeGroup)) {
      const clonedBiddersList = cloneDeep(biddersList);
      const selectedIDs = activeGroup.BidderIDs.split(',');
      selectedIDs.forEach((id) => {
        if (clonedBiddersList[id]) {
          clonedBiddersList[id].selected = true;
        }
      });
      setFilteredBiddersList(clonedBiddersList);
    }
  }, [activeGroup]);

  // convert the data to use the new table interface
  const rows = formatBiddersResponseIntoLVTRows(responseData, cfg.table);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(rows.map((n: any) => n.ID.value));
    } else {
      setSelected([]);
    }
  };

  const handleOnChangeSelect = (id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleOnClickBulkDelete = () => {
    setIsDeleteModalActive(true);
  };

  const menuTitle = () => {
    return (
      <Cols style={{ marginTop: '5px' }}>
        <Col>
          <AddIcon
            style={{
              color: publicaColors.blue,
            }}
          />
        </Col>
        <Col>
          <Text color={publicaColors.blue} bold style={{ fontSize: '14px' }} tKey='AddToGroup' />
        </Col>
      </Cols>
    );
  };

  const addToExistingRow = () => {
    return (
      <Col
        onClick={() => getBidderGroupsList(ESelectedModal.AddToBidderGroup)}
        style={{ width: '100%' }}
      >
        <Text style={{ fontSize: '14px' }}>Add to existing</Text>
      </Col>
    );
  };

  const createNewRow = () => {
    return (
      <Col
        onClick={() => getBidderGroupsList(ESelectedModal.GroupEditor)}
        style={{ width: '100%' }}
      >
        <Text style={{ fontSize: '14px' }}>Create new</Text>
      </Col>
    );
  };

  const handleCloseBulkDeleteModal = () => {
    unstable_batchedUpdates(() => {
      setIsDeleteModalActive(false);
      setSelected([]);
    });
  };

  const handleDeleteBulkBidders = () => {
    setIsTableActionPending(true);
    const numSelected = selected.length;
    const deleteHeaderBidders: Array<Promise<unknown>> = [];
    selected.forEach((id) => {
      deleteHeaderBidders.push(
        deleteHeaderBidder({
          query: {
            publisher_id: session.publisher.ID,
            bidder_id: id,
          },
        }),
      );
    });
    Promise.all(deleteHeaderBidders)
      .then(() => {
        track('Bidder List - Bulk Delete', { isSuperUser }, user.user);
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage(
            t('SnackbarSuccessfullyDeletedNumItems', { count: numSelected, item: t('Bidders') }),
          );
          setSnackbarError(false);
          setSelected([]);
        });
        getData();
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage(t('SnackbarFailedToDeleteItem', { item: t('Bidders') }));
          setSnackbarError(true);
        });
      });
    return Promise.resolve(undefined);
  };

  const addToExisting = {
    closeOnClick: true,
    render: addToExistingRow,
  };
  const createNew = {
    closeOnClick: true,
    render: createNewRow,
  };
  const menuButtonItems = [addToExisting, createNew];

  return (
    <Panels
      max
      vertical
      relative
      largeHorizontalPadding
      verticalPadding
      style={{ rowGap: spacing.medium }}
    >
      {/* TOP BUTTONS */}
      <Panel>
        <Cols
          wrap
          gap={spacing.small}
          style={{
            justifyContent: 'flex-end',
            alignItems: 'center',
            rowGap: spacing.small,
          }}
        >
          <Col>
            <Cols
              wrap
              gap={spacing.small}
              style={{
                justifyContent: 'flex-end',
                alignItems: 'center',
                rowGap: spacing.extraSmall,
              }}
            >
              {/* CREATE BIDDER BUTTON */}
              <Col>
                <ButtonV2
                  disabled={!writeAuthorization}
                  onClick={() => {
                    track('Bidder List - Create Bidder', { isSuperUser }, user.user);
                    navigate(RouteBidderSettingsNew, extractRouteParams({ session, user }));
                  }}
                  className={`cypress-${cypressClassName}-button-createbidder`}
                  tKey='CreateBidder'
                />
              </Col>
              {/* BULK UPLOAD BUTTON */}
              <Col>
                {!writeAuthorization.isAuthorized && (isSamsung || isSuperUser) ? (
                  <ChipInactive label='Read-Only' />
                ) : (
                  <ButtonV2
                    disabled={!writeAuthorization}
                    onClick={() => {
                      track('Bidder List - Bulk Upload', { isSuperUser }, user.user);
                      navigate(RouteBidderBulkUpload, extractRouteParams({ session, user }));
                    }}
                    className={`cypress-${cypressClassName}-button-bulkupload`}
                    tKey='BulkUpload'
                  />
                )}
              </Col>
            </Cols>
          </Col>
        </Cols>
      </Panel>
      <Panel highlighted>
        <Rows>
          <Row noPadding style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}>
            <FilterRowV2
              cypressParentName='bidderslistview'
              enableClear={true}
              filtersConfig={cfg.filters}
              filtersToOptionsMap={filters}
              handleApplySelect={handleFilterAccept}
              handleClearFilters={handleClearFilters}
              selectedFilters={selectedFilters}
              loadState={filtersLoadState}
            />
          </Row>
          <Row noPadding>
            <SortByRow
              sorts={sorts}
              currentOrderBy={orderBy}
              currentOrder={order}
              handleSelectSortBy={handleSelectSortBy}
            />
          </Row>
        </Rows>
      </Panel>
      <Panel highlighted largeHorizontalPadding verticalPadding>
        <Rows>
          <Row style={{ padding: '8px 0px' }}>
            <Cols style={{ justifyContent: 'space-between' }}>
              <Col>
                <Cols
                  wrap
                  gap={spacing.medium}
                  style={{
                    alignItems: 'center',
                    rowGap: spacing.small,
                  }}
                >
                  <Col>
                    <Cols
                      wrap
                      gap={spacing.medium}
                      style={{
                        alignItems: 'center',
                        rowGap: spacing.small,
                      }}
                    >
                      <Col>
                        <TextInput
                          enableClear
                          handleOnChange={handleOnChangeQuery}
                          inputStyle={
                            isSamsung ? { backgroundColor: publicaColors.veryLightGray } : {}
                          }
                          msDelay={500}
                          placeholder={t('SearchByNameID')}
                          prefix='search'
                        />
                      </Col>
                      {/* this adds another search box for bidder params (exclusively for samsung) */}
                      {isBidderParamsSearchEnabled && (
                        <Col>
                          <TextInput
                            enableClear
                            handleOnChange={handleOnChangeBidderParamsQuery}
                            msDelay={500}
                            placeholder={t('BiddersPlaceholderSearchByBidderParams')}
                            prefix='search_alt'
                          />
                        </Col>
                      )}
                    </Cols>
                  </Col>
                  <Col>
                    <SearchResultsText
                      numRowsDisplayed={responseData.length}
                      numTotalRows={totalRowCount}
                    />
                  </Col>
                </Cols>
              </Col>
              <Col>
                <Cols
                  wrap
                  gap={spacing.extraSmall}
                  style={{
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    rowGap: spacing.extraSmall,
                  }}
                >
                  {/* TRENDS TOGGLE */}
                  <Col>
                    <IconTextToggle
                      buttonStyle={{ padding: '5px 12px 4px' }}
                      handleOnClick={handleOnClickShowTrends}
                      isActive={isShowTrendsActive}
                      iconSlug='trends'
                      tKey='Trends'
                      tooltip={t('Trends')}
                    />
                  </Col>
                  <Col>
                    <ColumnSelector
                      // simple multi select props
                      preselectedColumnKeys={cfg.defaultState.activeColumnKeys}
                      resetActiveColumnKeys={cfg.columnSelector.resetKeys}
                      columnsList={activeColumnsList}
                      columnsHandleOnChangeOption={handleChangeActiveColumns}
                      columnsHandleOnAccept={handleAcceptActiveColumns}
                    />
                  </Col>
                </Cols>
              </Col>
            </Cols>
          </Row>
          <Row style={{ padding: '4px 4px 8px' }}>
            {selected.length > 1 && (
              <Cols
                wrap
                gap={spacing.small}
                style={{
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  rowGap: spacing.extraSmall,
                }}
              >
                <Col>
                  <Text small bold color={muiColors.checkbox}>
                    {`${selected.length} Bidders Selected`}
                  </Text>
                </Col>
                <Col>
                  <BulkUpdateButton selectedBidderIDs={selected} session={session} user={user} />
                </Col>
                <Col>
                  <Cols
                    gap={spacing.extraSmall}
                    style={{ alignItems: 'center', fontSize: fontSizing.small, height: '40px' }}
                    onClick={() => {
                      setForceMenuButtonClosed(false);
                    }}
                  >
                    {/* ADD TO BIDDER GROUP BUTTON */}
                    <MenuButton
                      marginTop={60}
                      forceClosed={forceMenuButtonClosed}
                      titleRender={menuTitle}
                      items={menuButtonItems}
                      minWidth={200}
                      enableDividers={false}
                    />
                  </Cols>
                </Col>
                <Col>
                  {/* BULK DELETE BUTTON */}
                  <ButtonV2
                    clearBackground
                    allCaps
                    onClick={handleOnClickBulkDelete}
                    style={{ padding: '5px 16px 5px 12px' }}
                  >
                    <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                      <Col paddingTop='6px'>
                        <DeleteIcon
                          style={{
                            height: '16px',
                            color: BULK_ACTION_COLOR,
                          }}
                        />
                      </Col>
                      <Col>
                        <Text small bold color={BULK_ACTION_COLOR} tKey='Delete' />
                      </Col>
                    </Cols>
                  </ButtonV2>
                </Col>
              </Cols>
            )}
          </Row>
          <Row noPadding>
            <ListViewTable
              activeColumnKeys={activeColumnKeys}
              columns={cfg.table.columns}
              customFunctions={{
                handleChangePriority,
                handleChangeSuperUserOnly,
                handleClickOnChannelsList,
                handleClickOnChannelGroupsList,
              }}
              displayNameWithID={!isDemoUser}
              enableActions
              enableSelectCheckbox
              enableStatusSwitch
              getItemSettingsRoute={getBidderSettingsRoute}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleCloneItem={handleCloneItem}
              handleDeleteItem={handleDeleteItem}
              handleOnChangeStatus={handleOnChangeStatus}
              handleOnChangeSelect={handleOnChangeSelect}
              handleOnClickColumnHeader={handleOnClickColumnHeader}
              handleSelectAllClick={handleSelectAllClick}
              isActionPending={isTableActionPending}
              isReadAuthorized={readAuthorization.isAuthorized}
              isSuperUser={isSuperUser}
              isWriteAuthorized={writeAuthorization.isAuthorized}
              loadState={loadState}
              order={order}
              orderBy={orderBy}
              page={page}
              rows={rows}
              rowsPerPage={rowsPerPage}
              selected={selected}
              totalRowCount={totalRowCount}
              className={cypressClassName}
            />
          </Row>
        </Rows>
      </Panel>
      <Dialog
        fullWidth
        maxWidth='md'
        keepMounted
        open={isChannelsModalActive}
        onClose={handleCloseModals}
      >
        <ModalSelectTable
          title={t('Channels')}
          singularTitle={t('Channel')}
          items={activeTargeted}
          onSelectRow={(siteID: string) => {
            navigate(RouteChannelExisting, extractRouteParams({ session, user }), {
              ch: siteID,
            });
          }}
          interactive={true}
        />
      </Dialog>
      <Dialog
        fullWidth
        maxWidth='md'
        keepMounted
        open={isChannelGroupsModalActive}
        onClose={handleCloseModals}
      >
        <ModalSelectTable
          title={t('ChannelGroups')}
          singularTitle={t('ChannelGroup')}
          items={activeTargeted}
          interactive={false}
        />
      </Dialog>
      <GroupSettingsDialog
        active={isGroupSettingsModalActive}
        groupInfo={activeGroup}
        list={filteredBiddersList}
        menuTitle='Bidders'
        onClose={handleCloseModals}
        onUpdate={(name: string) =>
          handleChangeBidderGroup(name, activeGroup ? Number(activeGroup.ID) : 0)
        }
        handleItemAcceptClick={handleAcceptCreateNewBidderSelect}
        selectedGroupLabels={
          (activeGroup && activeGroup.BidderGroupLabels) || selectedBidderGroupLabels
        }
        loadingGroups={loadState !== LoadingStateLoading}
        allGroupLabels={allGroupLabels}
        handleChangeSelectedGroupLabels={handleChangeSelectedBidderGroupLabels}
        showPrefixID={true}
      />
      {/* modals are a special case and can act as a panel */}
      <AddToBidderGroupDialog
        active={isAddToBidderGroupModalActive}
        list={bidderGroupsList}
        onClose={handleCloseModals}
        onUpdate={() => handleChangeBidderGroup('', activeGroup ? Number(activeGroup.ID) : 0)}
        handleItemAcceptClick={handleAcceptAddToBidderGroup}
        showPrefixID={true}
      />
      {/* modal to confirm bulk deletion. this is a special case and can act as a panel */}
      <ModelDialogDeleteView
        model={t('Bidders')}
        active={deleteBulkModalActive}
        Name={t('FormatNumItems', { count: selected.length, item: t('Bidders') })}
        deleteModel={() => {
          return handleDeleteBulkBidders();
        }}
        disableSnackbar
        onDelete={handleCloseBulkDeleteModal}
        onError={handleCloseBulkDeleteModal}
        onClose={handleCloseBulkDeleteModal}
      />
      <FlexibleBiddingDialog
        open={isTierChangeWarningActive}
        onRevert={() => setIsTierChangeWarningActive(false)}
        onConfirm={removeInvalidFlexibleBiddingThresholdsAndUpdate}
        invalidThresholds={flexibleBiddingInvalidThresholds}
      />
      {/* snackbar is a special case and can act as a panel */}
      <SnackbarView open={!!snackbarMessage} error={!!snackbarError} onClose={handleSnackbarClose}>
        {snackbarMessage}
      </SnackbarView>
      <PanelBreak height={spacing.large} />
    </Panels>
  );
};
